/*
 * GET-PROJECT-SUMMARY.JS - Project Summary Data Management Service
 * 
 * RECENT CHANGES - Hours Calculation Integration
 * @Author Samuel Paintsil
 * @Modified 2025-07-03
 * 
 * SUMMARY:
 * Enhanced project summary service to include forecast hours and actual hours calculations
 * for tasks and stages. Integrates timesheet data with labor forecasting to provide
 * comprehensive hour tracking across project sections.
 * 
 * NEW FEATURES ADDED:
 * 1. Timesheet Data Integration - Fetches approved timesheet entries for actual hours
 * 2. Hours Calculation Enhancement - Calculates forecast and actual hours by task
 * 3. Stage-level Hour Aggregation - Rolls up hours data to section/stage level
 * 4. Enhanced Section Data - Includes hours display formatting for UI
 * 
 * MAJOR FUNCTION MODIFICATIONS:
 * - getProjectSummaryData() - Added timesheet data fetching and hours calculations
 * - Enhanced section data processing with hours aggregation
 * - Updated Redux state to include enhanced section data with hours
 * 
 * DEPENDENCIES ADDED:
 * - getResourceAllocations() - For timesheet data retrieval
 * - calculateTasksHours() - For task-level hours calculation
 * - aggregateHoursByStage() - For stage-level hours aggregation
 * - formatHoursDisplay() - For consistent hours formatting
 */

import { addLevelAction, addProjectAction, addFinancialCardAction, addProgressCardAction, addOverviewCardsAction, addOptionsAction, addProjectTotalAction, addRevisedProjectTotalAction, addSectionsAction, addSectionDataAction, addOptionDataAction, addVariationDataAction, addProjectVariationAction, addSectionVariationAction, addProjectCardAction } from "../../../slice/project-summary";
import { getLevel } from "../../../actions/inventory.action";
import {
    getProjectSummary,
    getProgressClaimList,
    getPoCount,
    getvendorsPOInvoiceCount,
    getOptions,
    saveProjectType,
    getPOList,
} from "../../../actions/project-management.action";
import { getInvoiceList } from "../../../actions/invoice.action";
import idx from "idx";
import { setCookie } from "../../../util/auth";
import { stageTotal } from "../../../util/calculations/calculate-stage";
import { optionsTotal } from "../../../util/calculations/calculate-option";
import { getRevisedProjectTotal } from "./get-revised-calculation";
import { combineData, getVariationNumber } from "../../../util/common";
import { getWorkAllocationsData } from "./get-work-allocations";
import { getQuotationListData } from "./get-quotations";
import { getLaborCost } from "../../../actions/dashboard.action";
import { getResourceAllocations } from "../../../actions/timesheet.action";
import { getSubtaskLabors } from "../../../actions/project-management.action";
import { calculateTasksHours, aggregateHoursByStage, formatHoursDisplay, calculateActualHours, getVariance, getUtilisationRate, getEffortCompletionPercentage, getTimesheetHoursMap } from './hours-calculation';
import { roundValue, toDecimal } from "../../../util/decimal-utils";

// Helper to calculate hours for variations/options using their existing subTasks data and global subtasks
const calculateVariationHours = (variations, timesheetEntries, allSubtasks = []) => {
    return variations.map(variation => {

        // Variations may have subTasks (or subtasks) with labor data attached, 
        // or they may be linked to the global subtasks list via taskId
        let forecastHours = toDecimal(0);

        // 1. Get subtasks already attached to the variation object
        const attachedSubtasks = variation.subTasks || variation.subtasks || [];

        // 2. Get subtasks from the global project list that belong to this variation (id matches taskId)
        const linkedSubtasks = allSubtasks.filter(s => s.taskId === variation.id);

        // Deduplicate subtasks by id to ensure we don't double count if they are in both lists
        const variationSubtasksMap = new Map();
        [...attachedSubtasks, ...linkedSubtasks].forEach(s => {
            if (s.id && !variationSubtasksMap.has(s.id)) {
                variationSubtasksMap.set(s.id, s);
            }
        });
        const variationSubtasks = Array.from(variationSubtasksMap.values());

        // Calculate forecast hours from subtasks labor data
        if (Array.isArray(variationSubtasks)) {
            variationSubtasks.forEach(subtask => {
                if (subtask.labor && Array.isArray(subtask.labor)) {
                    subtask.labor.forEach(laborItem => {
                        try {
                            const laborData = typeof laborItem === 'string' ? JSON.parse(laborItem) : laborItem;
                            // Only include if is_personnel is true or undefined (Source of Truth)
                            if (laborData.is_personnel !== false && laborData.isPersonnel !== false) {
                                const qty = laborData.qty?.value !== undefined ? laborData.qty.value : (laborData.qty || laborData.quantity || 0);
                                forecastHours = forecastHours.plus(toDecimal(qty));
                            }
                        } catch (error) {
                            console.error('Error parsing labor data for variation:', error);
                        }
                    });
                }
            });
        }

        const totalForecastHours = forecastHours.toNumber();

        // Aggregate actual hours from all subtasks to accurately reflect total variation effort
        let totalActualHours = toDecimal(0);
        if (Array.isArray(variationSubtasks)) {
            variationSubtasks.forEach(subtask => {
                totalActualHours = totalActualHours.plus(toDecimal(calculateActualHours(subtask.id, timesheetEntries, 'TASK')));
            });
        }
        // Fallback: Check for hours logged directly against the variation itself (id matches entry.taskId)
        totalActualHours = totalActualHours.plus(toDecimal(calculateActualHours(variation.id, timesheetEntries, 'TASK')));

        const actualHours = totalActualHours.toNumber();
        const variance = getVariance(actualHours, totalForecastHours);
        const utilisationRate = getUtilisationRate(actualHours, totalForecastHours);
        const effortCompletionPercentage = getEffortCompletionPercentage(actualHours, totalForecastHours);

        return {
            ...variation,
            forecastHours: totalForecastHours,
            actualHours,
            forecastHoursDisplay: formatHoursDisplay(totalForecastHours),
            actualHoursDisplay: formatHoursDisplay(actualHours),
            variance: roundValue(variance, 0),
            utilisationRate: roundValue(utilisationRate, 0),
            effortCompletionPercentageValue: effortCompletionPercentage,
            effortCompletionPercentage: `${effortCompletionPercentage.toFixed(2)}%`,
        };
    });
};

// Helper to attach subtasks to their respective tasks for forecast hours calculation
const attachSubtasksToTasks = (sectionData, subtasks) => {
    // Create a map of subtasks grouped by taskId
    const subtaskMap = new Map();
    subtasks.forEach(subtask => {
        if (!subtaskMap.has(subtask.taskId)) {
            subtaskMap.set(subtask.taskId, []);
        }
        subtaskMap.get(subtask.taskId).push(subtask);
    });

    // Recursive function to attach subtasks to tasks in nested structure
    const attachSubtasksRecursively = (stages) => {
        return stages.map(stage => {
            if (stage.stages && Array.isArray(stage.stages)) {
                // Handle nested stages recursively
                return {
                    ...stage,
                    stages: attachSubtasksRecursively(stage.stages)
                };
            } else if (stage.stages === null && stage.tasks) {
                // Handle tasks at this level
                return {
                    ...stage,
                    tasks: (stage.tasks || []).map(task => ({
                        ...task,
                        subtasks: subtaskMap.get(task.id) || []
                    }))
                };
            } else {
                // Handle tasks directly on stage (fallback)
                return {
                    ...stage,
                    tasks: (stage.tasks || []).map(task => ({
                        ...task,
                        subtasks: subtaskMap.get(task.id) || []
                    }))
                };
            }
        });
    };

    return attachSubtasksRecursively(sectionData);
};

// Helper to calculate hours data for all tasks in all stages recursively
const calculateHoursRecursively = (stages, timesheetEntries, hoursMap = null) => {
    const localHoursMap = hoursMap || getTimesheetHoursMap(timesheetEntries);
    return stages.map(stage => {
        if (stage.stages && Array.isArray(stage.stages) && stage.stages.length > 0) {
            // Handle nested stages recursively
            const nestedStages = calculateHoursRecursively(stage.stages, timesheetEntries, localHoursMap);

            // Aggregate hours from all nested stages, PLUS any hours directly on this parent stage
            let totalForecastHours = toDecimal(0);
            let totalActualHours = toDecimal(calculateActualHours(stage.id, timesheetEntries, 'STAGE', localHoursMap));

            nestedStages.forEach(nestedStage => {
                totalForecastHours = totalForecastHours.plus(toDecimal(nestedStage.forecastHours || 0));
                totalActualHours = totalActualHours.plus(toDecimal(nestedStage.actualHours || 0));
            });

            const variance = getVariance(totalActualHours.toNumber(), totalForecastHours.toNumber());
            const utilisationRate = getUtilisationRate(totalActualHours.toNumber(), totalForecastHours.toNumber());
            const effortCompletionPercentage = getEffortCompletionPercentage(totalActualHours.toNumber(), totalForecastHours.toNumber());

            return {
                ...stage,
                stages: nestedStages,
                forecastHours: totalForecastHours.toNumber(),
                actualHours: totalActualHours.toNumber(),
                forecastHoursDisplay: formatHoursDisplay(totalForecastHours.toNumber()),
                actualHoursDisplay: formatHoursDisplay(totalActualHours.toNumber()),
                variance: roundValue(variance, 0),
                utilisationRate: roundValue(utilisationRate, 0),
                effortCompletionPercentageValue: effortCompletionPercentage,
                effortCompletionPercentage: `${effortCompletionPercentage.toFixed(2)}%`
            };
        } else {
            // Handle tasks at this level
            const stageTasks = stage.tasks || [];
            // Pass stage.id to include direct stage-level hours
            const stageHours = aggregateHoursByStage(stageTasks, timesheetEntries, stage.id, localHoursMap);
            const taskHoursData = calculateTasksHours(stageTasks, timesheetEntries, localHoursMap);

            return {
                ...stage,
                forecastHours: stageHours.totalForecastHours,
                actualHours: stageHours.totalActualHours,
                forecastHoursDisplay: formatHoursDisplay(stageHours.totalForecastHours),
                actualHoursDisplay: formatHoursDisplay(stageHours.totalActualHours),
                variance: roundValue(stageHours.variance, 0), // % over/under budget - whole numbers
                utilisationRate: roundValue(stageHours.utilisationRate, 0), // % of budget used - whole numbers
                effortCompletionPercentageValue: stageHours.effortCompletionPercentageValue,
                effortCompletionPercentage: stageHours.effortCompletionPercentage,

                tasks: stageTasks.map(task => ({
                    ...task,
                    ...(taskHoursData[task.id] || {})
                }))
            };
        }
    });
};

// Helper to calculate total hours from all stages (including nested) recursively
const calculateTotalProjectHours = (stagesList) => {
    let plannedEffort = toDecimal(0);
    let actualHours = toDecimal(0);

    // Sum top-level stages only, as children hours are already rolled up by calculateHoursRecursively
    stagesList.forEach(stage => {
        plannedEffort = plannedEffort.plus(toDecimal(stage.forecastHours || 0));
        actualHours = actualHours.plus(toDecimal(stage.actualHours || 0));
    });

    const variance = getVariance(actualHours.toNumber(), plannedEffort.toNumber());
    const utilisationRate = getUtilisationRate(actualHours.toNumber(), plannedEffort.toNumber());
    const effortCompletionPercentage = getEffortCompletionPercentage(actualHours.toNumber(), plannedEffort.toNumber());

    return {
        plannedEffort: plannedEffort.toNumber(),
        actualHours: actualHours.toNumber(),
        variance: roundValue(variance, 0),
        utilisationRate: roundValue(utilisationRate, 0),
        effortCompletionPercentageValue: effortCompletionPercentage,
        effortCompletionPercentage: `${effortCompletionPercentage.toFixed(2)}%`
    };
};

export const getProjectSummaryData = async ({ projectId, dispatch, type = null }) => {
    if (!projectId) return;
    let totalApprovedInvoices = 0;
    let totalClaimAmount = 0, totalRetentionAmount = 0;
    let showStageSelect = false;


    let overviewCards = {
        budgetCount: 0,
        purchaseOrderCount: 0,
        workOrderCount: 0,
        invoiceCount: 0,
        vendorInvoiceCount: 0,
        variationCount: 0,
        totalRFQSend: 0,
    }



    setCookie({ name: "projectId", value: projectId });

    const [
        progressClaims,
        invoices,
        totalRFQSend,
        poData,
        laborData,
        timesheetData,
        subtasksData,
        purchaseOrdersData,
        workOrdersListData,
        vendorInvoicesData,
        optionList,
        workAllocations
    ] = await Promise.all([
        dispatch(getProgressClaimList(projectId)),
        dispatch(getInvoiceList(projectId)),
        getQuotationListData({ projectId, dispatch }),
        dispatch(getPOList({ projectId, vendorType: "Supplier" })),
        dispatch(getLaborCost({ id: projectId })),
        dispatch(getResourceAllocations({ projectId })),
        dispatch(getSubtaskLabors(projectId)),
        dispatch(getPoCount(projectId)),
        dispatch(getPOList({ projectId, vendorType: "sub-contractor" })),
        dispatch(getvendorsPOInvoiceCount(projectId)),
        dispatch(getOptions(projectId)),
        getWorkAllocationsData({ projectId, dispatch })
    ]);

    const poList = poData?.list || [];
    const purchaseOrdersCount = purchaseOrdersData?.count || 0;
    // Filter work orders to exclude cancelled status
    const workOrdersList = workOrdersListData?.list || [];
    const workOrdersCount = workOrdersList.filter(wo => {
        const status = (wo.status || '').toLowerCase();
        return status !== 'cancelled';
    }).length;
    const vendorInvoiceCount = vendorInvoicesData?.invoicecount || 0;
    const variationCount = Array.isArray(optionList) ? optionList.length : 0;
    const { timeSpentProgress, timeAllocated } = workAllocations || { timeSpentProgress: 0, timeAllocated: 0 };

    progressClaims.forEach((claims) => {
        if (claims.progressClaimStatus && claims.progressClaimStatus != 'Draft') {
            const claimVal = claims.claimAmount ?? claims.claim_amount ?? claims.total_claim_amount ?? 0;
            totalClaimAmount = toDecimal(totalClaimAmount).plus(toDecimal(claimVal)).toNumber();
            totalRetentionAmount = toDecimal(totalRetentionAmount).plus(toDecimal(claims.retentionAmount || 0).minus(toDecimal(claims.retentionClaimAmount || 0))).toNumber();
            totalApprovedInvoices++;
        }
    });

    overviewCards.budgetCount = (Array.isArray(progressClaims) && progressClaims.length) || 0;
    overviewCards.invoiceCount = (Array.isArray(invoices) && invoices.length) || 0;
    overviewCards.purchaseOrderCount = purchaseOrdersCount;
    overviewCards.workOrderCount = workOrdersCount;
    overviewCards.vendorInvoiceCount = vendorInvoiceCount;
    overviewCards.variationCount = variationCount;
    overviewCards.totalRFQSend = totalRFQSend;

    const laborCost = idx(laborData, _ => _.taskData) || {};
    let resourceActualSpending = Object.values(laborCost).reduce((acc, value) => toDecimal(acc).plus(toDecimal(value)).toNumber(), 0);

    const timesheetEntries = Array.isArray(timesheetData) ? timesheetData : [];
    const subtasks = Array.isArray(subtasksData) ? subtasksData : [];

    return dispatch(getProjectSummary(projectId)).then((projectData) => {
        // Save the project type to Redux and cookies
        if (projectData.projectType) {
            dispatch(saveProjectType(projectData.projectType));
            setCookie({ name: "projectType", value: projectData.projectType });
        }

        getLevels({ levelId: projectData.level, dispatch });
        // 0. Extract variation and option data
        const isProjectView = type === 'project-view';
        const optionData = getVariationNumber(optionList);
        const variationData = isProjectView
            ? optionData.filter(_ => _.proposal_status === 'APPROVED')
            : optionData.filter(_ => !['REJECTED', 'CANCELLED'].includes(_.proposal_status));
        const projectLevelVariation = variationData.filter(_ => !_.stageId);
        const sectionLevelVariation = variationData.filter(_ => _.stageId);

        // 1. Apply hours calculation to all variations FIRST to ensure totals are accurate
        const enhancedVariationData = calculateVariationHours(variationData, timesheetEntries, subtasks);
        const enhancedOptionData = calculateVariationHours(optionData, timesheetEntries, subtasks);
        const displayableOptionData = isProjectView
            ? enhancedOptionData.filter(_ => _.proposal_status === 'APPROVED')
            : enhancedOptionData;
        const enhancedProjectLevelVariation = calculateVariationHours(projectLevelVariation, timesheetEntries, subtasks);
        const enhancedSectionLevelVariation = calculateVariationHours(sectionLevelVariation, timesheetEntries, subtasks);

        // 2. Now calculate option totals with enhanced hours data
        const optionCalculation = optionsTotal({ options: enhancedOptionData, projectType: projectData.type });
        const approvedOptionCalculation = isProjectView
            ? optionsTotal({ options: enhancedOptionData.filter(_ => _.proposal_status === 'APPROVED'), projectType: projectData.type })
            : optionCalculation;

        // 3. Add hours display formatting for the Variation Total row in UI
        if (optionCalculation && optionCalculation.projectTotal) {
            optionCalculation.projectTotal.forecastHoursDisplay = formatHoursDisplay(optionCalculation.projectTotal.forecastHours);
            optionCalculation.projectTotal.actualHoursDisplay = formatHoursDisplay(optionCalculation.projectTotal.actualHours);
            optionCalculation.projectTotal.variance = getVariance(optionCalculation.projectTotal.actualHours, optionCalculation.projectTotal.forecastHours);
            optionCalculation.projectTotal.utilisationRate = getUtilisationRate(optionCalculation.projectTotal.actualHours, optionCalculation.projectTotal.forecastHours);
            optionCalculation.projectTotal.effortCompletionPercentage = getEffortCompletionPercentage(optionCalculation.projectTotal.actualHours, optionCalculation.projectTotal.forecastHours);
        }

        let stages = idx(projectData, _ => _.stages) || [];
        const sectionData = combineData({ stages, variation: enhancedVariationData }) || [];

        const projectcard = {
            projectName: projectData.name,
            address: projectData.address,
            addressBreakdown: projectData.addressBreakdown,
            customer: projectData.customer ? projectData.customer.businessName : '',
            contact: [idx(projectData, _ => _.customerContact.firstName), idx(projectData, _ => _.customerContact.lastName)].filter(item => item).join(' '),
            assignedTo: projectData.manager ? projectData.manager.name : '',
        };
        const progresscard = {
            materialAllowance: 0,
            resourceAllowance: 0,
            contractorAllowance: 0,
            remainingMaterialBudget: 0,
            remainingResourceBudget: 0,
            remainingContractorBudget: 0,
            materialActualSpending: 0,
            resourceActualSpending: resourceActualSpending,
            contractorActualSpending: 0,
            timeSpend: timeSpentProgress,
            timeAllocated: timeAllocated,
        };
        const financialCard = {
            contract: 0.00,
            variations: 0.00,
            revisedContract: 0.00,
            claimed: 0.00,
            retentionHeld: 0.00,
            balance: 0.00,
        };

        const processStages = (stagesData) => {
            if (Array.isArray(stagesData)) {
                return stagesData.forEach((stage) => {
                    if (stage.stages) {
                        processStages(stage.stages);
                    } else if (stage.stages === null && stage.tasks) {
                        stage.tasks.forEach((task, index) => {
                            (task.material || []).forEach((material, mIndex) => {
                                if (typeof material === 'string') {
                                    task.material[mIndex] = JSON.parse(material);
                                }
                            });
                        });
                    }
                });
            }
        }

        const stageData = stageTotal(sectionData);
        let revisedProjectTotal = getRevisedProjectTotal({ sectionProjectTotal: stageData.projectTotal, optionProjectTotal: approvedOptionCalculation.projectTotal });

        const sectionDataWithSubtasks = attachSubtasksToTasks(sectionData, subtasks);
        const hoursMap = getTimesheetHoursMap(timesheetEntries);
        const enhancedSectionData = calculateHoursRecursively(sectionDataWithSubtasks, timesheetEntries, hoursMap);

        // Map recursive hour calculations back to the summarized sections object
        const mapHoursToSections = (sections, enhancedStages) => {
            enhancedStages.forEach(enhancedStage => {
                if (sections[enhancedStage.id]) {
                    sections[enhancedStage.id] = {
                        ...sections[enhancedStage.id],
                        forecastHours: enhancedStage.forecastHours,
                        actualHours: enhancedStage.actualHours,
                        forecastHoursDisplay: enhancedStage.forecastHoursDisplay,
                        actualHoursDisplay: enhancedStage.actualHoursDisplay,
                        variance: enhancedStage.variance,
                        utilisationRate: enhancedStage.utilisationRate,
                        effortCompletionPercentageValue: enhancedStage.effortCompletionPercentageValue,
                        effortCompletionPercentage: enhancedStage.effortCompletionPercentage,
                        tasks: enhancedStage.tasks || []
                    };
                }
                if (enhancedStage.stages && enhancedStage.stages.length > 0) {
                    mapHoursToSections(sections, enhancedStage.stages);
                }
            });
        };
        mapHoursToSections(stageData.sections, enhancedSectionData);

        // 1. Build a global tasks hours map from the recursive calculation results
        const globalTaskHoursMap = {};
        const buildGlobalTaskHoursMap = (enhancedStages) => {
            enhancedStages.forEach(stage => {
                if (stage.tasks && Array.isArray(stage.tasks)) {
                    stage.tasks.forEach(task => {
                        globalTaskHoursMap[task.id] = {
                            forecastHours: task.forecastHours,
                            actualHours: task.actualHours,
                            forecastHoursDisplay: task.forecastHoursDisplay,
                            actualHoursDisplay: task.actualHoursDisplay,
                            variance: task.variance,
                            utilisationRate: task.utilisationRate,
                            effortCompletionPercentageValue: task.effortCompletionPercentageValue,
                            effortCompletionPercentage: task.effortCompletionPercentage,
                        };
                    });
                }
                if (stage.stages && stage.stages.length > 0) {
                    buildGlobalTaskHoursMap(stage.stages);
                }
            });
        };
        buildGlobalTaskHoursMap(enhancedSectionData);

        // 2. Enrich individual tasks stored within each section's taskList
        Object.keys(stageData.sections).forEach(sectionId => {
            const section = stageData.sections[sectionId];
            if (section.taskList && section.taskList.tasks) {
                Object.keys(section.taskList.tasks).forEach(taskId => {
                    if (globalTaskHoursMap[taskId]) {
                        section.taskList.tasks[taskId] = {
                            ...section.taskList.tasks[taskId],
                            ...globalTaskHoursMap[taskId]
                        };
                    }
                });
            }
        });

        // --- Start of additions for projectTotal and revisedProjectTotal hours ---
        const { plannedEffort, actualHours } = calculateTotalProjectHours(enhancedSectionData);
        let totalProjectPlannedEffort = plannedEffort;
        let totalProjectActualHours = actualHours;

        const projectTotalVariance = getVariance(totalProjectActualHours, totalProjectPlannedEffort);
        const projectTotalUtilisationRate = getUtilisationRate(totalProjectActualHours, totalProjectPlannedEffort);
        const projectTotalEffortCompletionPercentage = getEffortCompletionPercentage(totalProjectActualHours, totalProjectPlannedEffort);

        // Update projectTotal with hours
        stageData.projectTotal = {
            ...stageData.projectTotal,
            forecastHours: totalProjectPlannedEffort,
            actualHours: totalProjectActualHours,
            forecastHoursDisplay: formatHoursDisplay(totalProjectPlannedEffort),
            actualHoursDisplay: formatHoursDisplay(totalProjectActualHours),
            variance: roundValue(projectTotalVariance, 0),
            utilisationRate: roundValue(projectTotalUtilisationRate, 0),
            effortCompletionPercentageValue: projectTotalEffortCompletionPercentage,
            effortCompletionPercentage: `${projectTotalEffortCompletionPercentage.toFixed(2)}%`,
        };

        // Recalculate projectTotal's percentageComplete based on Financials (Total Claimed / Contract),
        // mirroring the revisedProjectTotal fix below, so the "Project Total" row (which reads from
        // stageData.projectTotal, not revisedProjectTotal) also reflects approved progress claims.
        if (toDecimal(stageData.projectTotal.totalSaleAmount).gt(0)) {
            const projectTotalFinancialPercentage = roundValue(
                toDecimal(totalClaimAmount).dividedBy(toDecimal(stageData.projectTotal.totalSaleAmount)).times(100)
            );
            stageData.projectTotal.percentageComplete = projectTotalFinancialPercentage;
            stageData.projectTotal.completionPercentage = `${roundValue(projectTotalFinancialPercentage, 0)}%`;
        }

        // Enrich variation hours into option stage summary and project total
        if (optionCalculation) {
            // 1. Enrich stage summaries (Variation Total rows)
            if (optionCalculation.stageSummary) {
                Object.keys(optionCalculation.stageSummary).forEach(stageId => {
                    const summary = optionCalculation.stageSummary[stageId];
                    const f = summary.forecastHours || 0;
                    const a = summary.actualHours || 0;
                    const v = getVariance(a, f);
                    const u = getUtilisationRate(a, f);
                    const c = getEffortCompletionPercentage(a, f);

                    optionCalculation.stageSummary[stageId] = {
                        ...summary,
                        forecastHoursDisplay: formatHoursDisplay(f),
                        actualHoursDisplay: formatHoursDisplay(a),
                        variance: roundValue(v, 0),
                        utilisationRate: roundValue(u, 0),
                        effortCompletionPercentageValue: c,
                        effortCompletionPercentage: `${c.toFixed(2)}%`,
                    };

                    // 2. Enrich individual variations within this stage's optionList (Object map)
                    if (summary.optionList && typeof summary.optionList === 'object') {
                        Object.keys(summary.optionList).forEach(optionId => {
                            const variationItem = summary.optionList[optionId];
                            const enhancedV = enhancedVariationData.find(v => String(v.id) === String(optionId));
                            if (enhancedV) {
                                Object.assign(variationItem, {
                                    forecastHours: enhancedV.forecastHours,
                                    actualHours: enhancedV.actualHours,
                                    forecastHoursDisplay: enhancedV.forecastHoursDisplay,
                                    actualHoursDisplay: enhancedV.actualHoursDisplay,
                                    variance: enhancedV.variance,
                                    utilisationRate: enhancedV.utilisationRate,
                                    effortCompletionPercentageValue: enhancedV.effortCompletionPercentageValue,
                                    effortCompletionPercentage: enhancedV.effortCompletionPercentage,
                                });
                            }
                        });
                    }
                });
            }

            // 3. Enrich overall options project total
            const of = optionCalculation.projectTotal?.forecastHours || 0;
            const oa = optionCalculation.projectTotal?.actualHours || 0;
            const ov = getVariance(oa, of);
            const ou = getUtilisationRate(oa, of);
            const oc = getEffortCompletionPercentage(oa, of);

            optionCalculation.projectTotal = {
                ...optionCalculation.projectTotal,
                forecastHoursDisplay: formatHoursDisplay(of),
                actualHoursDisplay: formatHoursDisplay(oa),
                variance: roundValue(ov, 0),
                utilisationRate: roundValue(ou, 0),
                effortCompletionPercentageValue: oc,
                effortCompletionPercentage: `${oc.toFixed(2)}%`,
            };
        }

        // Add hours from ALL variations to totals for the Revised Project Total
        let finalPlannedEffort = toDecimal(totalProjectPlannedEffort);
        let finalActualHours = toDecimal(totalProjectActualHours);

        enhancedVariationData.forEach(variation => {
            finalPlannedEffort = finalPlannedEffort.plus(toDecimal(variation.forecastHours || 0));
            finalActualHours = finalActualHours.plus(toDecimal(variation.actualHours || 0));
        });

        const revisedProjectTotalVariance = getVariance(finalActualHours.toNumber(), finalPlannedEffort.toNumber());
        const revisedProjectTotalUtilisationRate = getUtilisationRate(finalActualHours.toNumber(), finalPlannedEffort.toNumber());
        const revisedProjectTotalEffortCompletionPercentage = getEffortCompletionPercentage(finalActualHours.toNumber(), finalPlannedEffort.toNumber());

        // Update revisedProjectTotal with summed hours from both project and variations
        revisedProjectTotal = {
            ...revisedProjectTotal,
            forecastHours: finalPlannedEffort.toNumber(),
            actualHours: finalActualHours.toNumber(),
            forecastHoursDisplay: formatHoursDisplay(finalPlannedEffort.toNumber()),
            actualHoursDisplay: formatHoursDisplay(finalActualHours.toNumber()),
            variance: roundValue(revisedProjectTotalVariance, 0),
            utilisationRate: roundValue(revisedProjectTotalUtilisationRate, 0),
            effortCompletionPercentageValue: revisedProjectTotalEffortCompletionPercentage,
            effortCompletionPercentage: `${revisedProjectTotalEffortCompletionPercentage.toFixed(2)}%`,
        };

        // Recalculate percentageComplete based on Financials (Total Claimed / Revised Contract)
        // This ensures Section-level claims are captured even if task percentages aren't updated
        if (toDecimal(revisedProjectTotal.totalSaleAmount).gt(0)) {
            const financialPercentage = roundValue(
                toDecimal(totalClaimAmount).dividedBy(toDecimal(revisedProjectTotal.totalSaleAmount)).times(100)
            );
            revisedProjectTotal.percentageComplete = financialPercentage;
            revisedProjectTotal.completionPercentage = `${roundValue(financialPercentage, 0)}%`;
        }

        // Also update stage-level percentageComplete if it's 0 but there are claims
        // This fixes the issue where an Attributed Section has claims but 0% task completion
        if (progressClaims && Array.isArray(progressClaims)) {
            const sectionClaims = {};

            // 1. Build a taskId -> stageId mapping from sectionData for task-level claims
            // This is needed to aggregate historical task-level claims (made before attribution level was set)
            const taskToStageMap = {};
            const attributionLevelStages = new Set();
            const stageChildrenMap = {}; // stageId -> [childStageIds]

            const buildTaskStageMapping = (stagesArray, parentId = null) => {
                stagesArray.forEach(stage => {
                    // Track attribution level stages
                    if (stage.is_attribution_level) {
                        attributionLevelStages.add(stage.id);
                    }

                    // Build parent -> children map for rolling up claims
                    if (parentId) {
                        if (!stageChildrenMap[parentId]) {
                            stageChildrenMap[parentId] = [];
                        }
                        stageChildrenMap[parentId].push(stage.id);
                    }

                    if (stage.stages && Array.isArray(stage.stages)) {
                        // Nested stages - recurse
                        buildTaskStageMapping(stage.stages, stage.id);
                    } else if (stage.tasks && Array.isArray(stage.tasks)) {
                        // Map each task to its stage
                        stage.tasks.forEach(task => {
                            if (task.id) {
                                taskToStageMap[task.id] = stage.id;
                            }
                        });
                    }
                });
            };
            buildTaskStageMapping(sectionData);

            // 2. Build parent-child relationships to find attribution-level ancestors
            const stageParentMap = {};
            const buildParentMap = (stagesArray, parentId = null) => {
                stagesArray.forEach(stage => {
                    if (parentId) {
                        stageParentMap[stage.id] = parentId;
                    }
                    if (stage.stages && Array.isArray(stage.stages)) {
                        buildParentMap(stage.stages, stage.id);
                    }
                });
            };
            buildParentMap(sectionData);

            // 3. Helper to get all ancestors for a given stageId (including the stage itself)
            const getAllAncestors = (stageId) => {
                const ancestors = [stageId];
                let currentId = stageId;
                while (stageParentMap[currentId]) {
                    ancestors.push(stageParentMap[currentId]);
                    currentId = stageParentMap[currentId];
                }
                return ancestors;
            };

            // 4. Aggregate claims by stageId - each claim contributes to its stage AND all ancestors
            progressClaims.forEach(claim => {
                // Use 'progressClaimTask' as defined in backend service alias, fallback to legacy if needed
                const tasks = claim.progressClaimTask || claim.tbl_progress_claim_tasks;
                if (claim.progressClaimStatus && claim.progressClaimStatus != 'Draft' && Array.isArray(tasks)) {
                    // Protections against double counting within a single claim
                    const stagesWithSectionClaims = new Set(
                        tasks.filter(t => !t.isDelete && t.type === 'SECTION').map(t => t.stageId)
                    );

                    tasks.filter(t => !t.isDelete).forEach(taskClaim => {
                        let targetStageId = null;

                        if (taskClaim.stageId) {
                            // Section-level claim - use stageId directly
                            targetStageId = taskClaim.stageId;
                        } else if (taskClaim.taskId) {
                            // Task-level claim - find the stage this task belongs to
                            targetStageId = taskToStageMap[taskClaim.taskId];
                        }

                        if (targetStageId) {
                            // Accuracy check: if this is a task claim, only count it if its stage doesn't have a section claim
                            if (taskClaim.type === 'SECTION' || !stagesWithSectionClaims.has(taskClaim.stageId)) {
                                // Add claim amount to this stage AND all its ancestors
                                const claimAmount = parseFloat(taskClaim.total) || 0;
                                const ancestors = getAllAncestors(targetStageId);
                                ancestors.forEach(ancestorId => {
                                    sectionClaims[ancestorId] = (sectionClaims[ancestorId] || 0) + claimAmount;
                                });
                            }
                        }
                    });
                }
            });

            // 5. Update ALL section percentages using financial completion (claim / sale)
            // This ensures consistency with the Budget page which uses the same calculation
            if (stageData.sections) {
                Object.keys(stageData.sections).forEach(sectionId => {
                    const section = stageData.sections[sectionId];
                    const claimTotal = sectionClaims[sectionId] || 0;

                    // Always use financial percentage for accurate completion tracking
                    // This handles: attribution sections, parent sections with nested attribution children,
                    // and sections with mixed historical task claims and new section claims
                    if (section.totalSaleAmount > 0 && claimTotal > 0) {
                        const financialPercentage = Math.min(roundValue((claimTotal / section.totalSaleAmount) * 100), 100);
                        section.percentageComplete = financialPercentage;
                        // Non-nested sections render from taskList.total (see project-task-list.jsx
                        // hasNestedStages check), so this must be kept in sync too.
                        if (section.taskList && section.taskList.total) {
                            section.taskList.total.percentageComplete = financialPercentage;
                        }
                        // Propagate down to each individual task row too - otherwise the "Total"/
                        // section-level completion % is fixed but each task row (which reads its own
                        // taskList.tasks[taskId].percentageComplete, an unrelated stale field from the
                        // task record) keeps showing its old, pre-claim value even though the Budget
                        // page (which derives per-task completion live from claim data) shows 100%.
                        if (section.taskList && section.taskList.tasks) {
                            Object.values(section.taskList.tasks).forEach(task => {
                                task.percentageComplete = financialPercentage;
                            });
                        }
                    }
                });
            }
        }
        // --- End of additions ---

        progresscard.materialAllowance = revisedProjectTotal.materialCost;
        progresscard.resourceAllowance = revisedProjectTotal.laborCost;
        progresscard.contractorAllowance = revisedProjectTotal.subcontractorCost;

        financialCard.contract = stageData.projectTotal.totalSaleAmount;
        financialCard.variations = approvedOptionCalculation.projectTotal.totalSaleAmount;
        financialCard.revisedContract = revisedProjectTotal.totalSaleAmount;
        financialCard.claimed = totalClaimAmount;
        financialCard.retentionHeld = totalRetentionAmount;
        financialCard.balance = financialCard.revisedContract - (financialCard.claimed - financialCard.retentionHeld);


        const getMaterialWorkDone = (data = []) => {
            // Object to store sums by taskId
            const costByTaskId = {};
            let totalCost = 0;

            // Iterate through each purchase order
            data.forEach(po => {
                if (po.status !== 'Completed') return;
                // Iterate through each item in the purchase order
                if (po.items && Array.isArray(po.items)) {
                    po.items.forEach(item => {
                        if (!item) return;
                        // Extract taskId and cost
                        const taskId = item.taskId;
                        const cost = isNaN(parseFloat(item.total)) ? 0 : parseFloat(item.total) // Ensure total is a number

                        // Initialize or add to the sum for this taskId
                        if (!costByTaskId[taskId]) {
                            costByTaskId[taskId] = 0;
                        }
                        totalCost += cost;
                        costByTaskId[taskId] += cost;
                    });
                }
            });

            // Convert to array of objects for easier output and round to 2 decimal places
            const result = Object.keys(costByTaskId).map(taskId => ({
                taskId,
                totalCost: costByTaskId[taskId]
            }));
            progresscard.materialActualSpending = totalCost;

            return { result, totalCost };
        }

        const variations = Array.isArray(optionList) && optionList.length > 0 ?
            optionList.filter(_ => !['REJECTED', 'CANCELLED'].includes(_.proposal_status) && !_.stageId) : [];

        variations.map((variation) => {
            const tasksActualBudget = calculateActualBudget(variation);
            // progresscard.materialActualSpending += tasksActualBudget.actualMaterialCost || 0;
            progresscard.contractorActualSpending += tasksActualBudget.actualSubContractorCost || 0;
        });


        const calculateStageProgress = (stagesData) => {

            stagesData.forEach((stage) => {
                if (stage.stages) {
                    calculateStageProgress(stage.stages);
                } else if (stage.stages === null && stage.tasks) {
                    if (stage.tasks.length > 0) {
                        stage.tasks.map((task) => {
                            const tasksActualBudget = calculateActualBudget(task);
                            // progresscard.materialActualSpending += tasksActualBudget.actualMaterialCost || 0;
                            progresscard.contractorActualSpending += tasksActualBudget.actualSubContractorCost || 0;
                        });

                    }

                }
            });
        }

        if (projectData.stages !== null) {
            processStages(stages);
            getMaterialWorkDone(poList);
            calculateStageProgress(sectionData);
            progresscard.remainingMaterialBudget = ((revisedProjectTotal.materialCost || 0) - progresscard.materialActualSpending);
            progresscard.remainingResourceBudget = ((revisedProjectTotal.laborCost || 0) - progresscard.resourceActualSpending)
            progresscard.remainingContractorBudget = ((revisedProjectTotal.subcontractorCost || 0) - progresscard.contractorActualSpending);

            // Update Time Spend bar to use Revised Project Totals (Base + Variations)
            const hoursToTime = (h) => `${Math.floor(h)}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`;
            progresscard.timeSpend = hoursToTime(finalActualHours.toNumber());
            progresscard.timeAllocated = hoursToTime(finalPlannedEffort.toNumber());
        }

        dispatch(addProjectCardAction(projectcard));
        dispatch(addFinancialCardAction(financialCard));
        dispatch(addProgressCardAction(progresscard));
        dispatch(addOverviewCardsAction(overviewCards));
        dispatch(addProjectTotalAction(stageData.projectTotal));
        dispatch(addRevisedProjectTotalAction(revisedProjectTotal));
        dispatch(addSectionsAction(stageData.sections));
        dispatch(addOptionsAction(approvedOptionCalculation));
        dispatch(addSectionDataAction(enhancedSectionData));
        dispatch(addOptionDataAction(displayableOptionData));
        dispatch(addVariationDataAction(enhancedVariationData));
        dispatch(addProjectVariationAction(enhancedProjectLevelVariation));
        dispatch(addSectionVariationAction(enhancedSectionLevelVariation));

        delete projectData.stages;

        dispatch(addProjectAction({
            projectId: projectData.id,
            projectName: projectData.name,
            projectManagerName: idx(projectData, _ => _.manager.name),
            quoteType: projectData.quoteType,
            projectDescription: projectData.description,
            customerName: projectData.customer && projectData.customer.businessName,
            hasStages: projectData.hasStages,
            projectType: projectData.type,
            projectDataType: projectData.projectType,
            level: projectData.level,
            customerLevel: projectData.customer ? projectData.customer.level : "",
            contactData: projectData.customerContact,
            projectMarginValue: projectData.profitMarginValue || "",
            status: projectData.status,
            siteAddress: projectData.address,
            projectDueDate: projectData.dateOfCreation,
            attachmentArr: projectData.attachment,
            showStageSelect,
            addressBreakdown: projectData.addressBreakdown,
            totalApprovedInvoices,
            res: projectData,
        }))

    });
}


export const updateProject = async ({ projectId, dispatch, type = null }) => {
    if (!projectId) return;
    let totalApprovedInvoices = 0;
    let showStageSelect = false;

    dispatch(getProjectSummary(projectId)).then((projectData) => {
        // Save the project type to Redux and cookies
        if (projectData.projectType) {
            dispatch(saveProjectType(projectData.projectType));
            setCookie({ name: "projectType", value: projectData.projectType });
        }

        dispatch(addProjectAction({
            projectId: projectData.id,
            projectName: projectData.name,
            projectManagerName: idx(projectData, _ => _.manager.name),
            quoteType: projectData.quoteType,
            projectDescription: projectData.description,
            customerName: projectData.customer && projectData.customer.businessName,
            hasStages: projectData.hasStages,
            projectType: projectData.type,
            projectDataType: projectData.projectType,
            level: projectData.level,
            customerLevel: projectData.customer ? projectData.customer.level : "",
            contactData: projectData.customerContact,
            projectMarginValue: projectData.profitMarginValue || "",
            status: projectData.status,
            siteAddress: projectData.address,
            projectDueDate: projectData.dateOfCreation,
            attachmentArr: projectData.attachment,
            showStageSelect,
            totalApprovedInvoices,
            res: projectData
        }))

    });
}

export const updateProjectSummaryData = async ({ projectId, dispatch, type = null }) => {
    if (!projectId) return;
    let totalApprovedInvoices = 0;
    let showStageSelect = false;



    setCookie({ name: "projectId", value: projectId });

    let totalClaimAmount = 0;
    const progressClaims = await dispatch(getProgressClaimList(projectId));

    if (Array.isArray(progressClaims)) {
        progressClaims.forEach((claims) => {
            if (claims.progressClaimStatus && claims.progressClaimStatus != 'Draft') {
                totalClaimAmount += claims.claimAmount;
                totalApprovedInvoices++;
            }
        });
    }

    // Fetch timesheet data for hours calculation
    const timesheetData = await dispatch(getResourceAllocations({ projectId }));
    const timesheetEntries = Array.isArray(timesheetData) ? timesheetData : [];

    // Fetch subtasks data for forecast hours calculation
    const subtasksData = await dispatch(getSubtaskLabors(projectId));
    const subtasks = Array.isArray(subtasksData) ? subtasksData : [];

    const optionList = await dispatch(getOptions(projectId));

    dispatch(getProjectSummary(projectId)).then((projectData) => {
        // Save the project type to Redux and cookies
        if (projectData.projectType) {
            dispatch(saveProjectType(projectData.projectType));
            setCookie({ name: "projectType", value: projectData.projectType });
        }

        getLevels({ levelId: projectData.level, dispatch });
        // 0. Extract variation and option data
        const isProjectView = type === 'project-view';
        const optionData = getVariationNumber(optionList);
        const variationData = isProjectView
            ? optionData.filter(_ => _.proposal_status === 'APPROVED')
            : optionData.filter(_ => !['REJECTED', 'CANCELLED'].includes(_.proposal_status));
        const projectLevelVariation = variationData.filter(_ => !_.stageId);
        const sectionLevelVariation = variationData.filter(_ => _.stageId);

        // 1. Apply hours calculation to all variations FIRST to ensure totals are accurate
        const enhancedVariationData = calculateVariationHours(variationData, timesheetEntries, subtasks);
        const enhancedOptionData = calculateVariationHours(optionData, timesheetEntries, subtasks);
        const displayableOptionData = isProjectView
            ? enhancedOptionData.filter(_ => _.proposal_status === 'APPROVED')
            : enhancedOptionData;
        const enhancedProjectLevelVariation = calculateVariationHours(projectLevelVariation, timesheetEntries, subtasks);
        const enhancedSectionLevelVariation = calculateVariationHours(sectionLevelVariation, timesheetEntries, subtasks);

        // 2. Now calculate option totals with enhanced hours data
        const optionCalculation = optionsTotal({ options: enhancedOptionData, projectType: projectData.type });
        const approvedOptionCalculation = isProjectView
            ? optionsTotal({ options: enhancedOptionData.filter(_ => _.proposal_status === 'APPROVED'), projectType: projectData.type })
            : optionCalculation;

        // 3. Add hours display formatting and aggregates for the Variation Total row in UI
        if (optionCalculation && optionCalculation.projectTotal) {
            optionCalculation.projectTotal.forecastHoursDisplay = formatHoursDisplay(optionCalculation.projectTotal.forecastHours);
            optionCalculation.projectTotal.actualHoursDisplay = formatHoursDisplay(optionCalculation.projectTotal.actualHours);
            optionCalculation.projectTotal.variance = getVariance(optionCalculation.projectTotal.actualHours, optionCalculation.projectTotal.forecastHours);
            optionCalculation.projectTotal.utilisationRate = getUtilisationRate(optionCalculation.projectTotal.actualHours, optionCalculation.projectTotal.forecastHours);
            optionCalculation.projectTotal.effortCompletionPercentageValue = getEffortCompletionPercentage(optionCalculation.projectTotal.actualHours, optionCalculation.projectTotal.forecastHours);
            optionCalculation.projectTotal.effortCompletionPercentage = `${optionCalculation.projectTotal.effortCompletionPercentageValue.toFixed(2)}%`;
        }

        let stages = idx(projectData, _ => _.stages) || [];
        const sectionData = combineData({ stages, variation: enhancedVariationData }) || [];


        const processStages = (stagesData) => {
            if (Array.isArray(stagesData)) {
                return stagesData.forEach((stage) => {
                    if (stage.stages) {
                        processStages(stage.stages);
                    } else if (stage.stages === null && stage.tasks) {
                        stage.tasks.forEach((task, index) => {
                            (task.material || []).forEach((material, mIndex) => {
                                if (typeof material === 'string') {
                                    task.material[mIndex] = JSON.parse(material);
                                }
                            });
                        });
                    }
                });
            }
        }


        const stageData = stageTotal(sectionData);
        let revisedProjectTotal = getRevisedProjectTotal({ sectionProjectTotal: stageData.projectTotal, optionProjectTotal: approvedOptionCalculation.projectTotal });

        if (projectData.stages !== null) processStages(projectData.stages);

        const sectionDataWithSubtasks = attachSubtasksToTasks(sectionData, subtasks);
        const hoursMap = getTimesheetHoursMap(timesheetEntries);
        const enhancedSectionData = calculateHoursRecursively(sectionDataWithSubtasks, timesheetEntries, hoursMap);

        // Map recursive hour calculations back to the summarized sections object
        const mapHoursToSections = (sections, enhancedStages) => {
            enhancedStages.forEach(enhancedStage => {
                if (sections[enhancedStage.id]) {
                    sections[enhancedStage.id] = {
                        ...sections[enhancedStage.id],
                        forecastHours: enhancedStage.forecastHours,
                        actualHours: enhancedStage.actualHours,
                        forecastHoursDisplay: enhancedStage.forecastHoursDisplay,
                        actualHoursDisplay: enhancedStage.actualHoursDisplay,
                        variance: enhancedStage.variance,
                        utilisationRate: enhancedStage.utilisationRate,
                        effortCompletionPercentageValue: enhancedStage.effortCompletionPercentageValue,
                        effortCompletionPercentage: enhancedStage.effortCompletionPercentage,
                        tasks: enhancedStage.tasks || []
                    };
                }
                if (enhancedStage.stages && enhancedStage.stages.length > 0) {
                    mapHoursToSections(sections, enhancedStage.stages);
                }
            });
        };
        mapHoursToSections(stageData.sections, enhancedSectionData);

        // 1. Build a global tasks hours map from the recursive calculation results
        const globalTaskHoursMap = {};
        const buildGlobalTaskHoursMap = (enhancedStages) => {
            enhancedStages.forEach(stage => {
                if (stage.tasks && Array.isArray(stage.tasks)) {
                    stage.tasks.forEach(task => {
                        globalTaskHoursMap[task.id] = {
                            forecastHours: task.forecastHours,
                            actualHours: task.actualHours,
                            forecastHoursDisplay: task.forecastHoursDisplay,
                            actualHoursDisplay: task.actualHoursDisplay,
                            variance: task.variance,
                            utilisationRate: task.utilisationRate,
                            effortCompletionPercentageValue: task.effortCompletionPercentageValue,
                            effortCompletionPercentage: task.effortCompletionPercentage,
                        };
                    });
                }
                if (stage.stages && stage.stages.length > 0) {
                    buildGlobalTaskHoursMap(stage.stages);
                }
            });
        };
        buildGlobalTaskHoursMap(enhancedSectionData);

        // 2. Enrich individual tasks stored within each section's taskList
        Object.keys(stageData.sections).forEach(sectionId => {
            const section = stageData.sections[sectionId];
            if (section.taskList && section.taskList.tasks) {
                Object.keys(section.taskList.tasks).forEach(taskId => {
                    if (globalTaskHoursMap[taskId]) {
                        section.taskList.tasks[taskId] = {
                            ...section.taskList.tasks[taskId],
                            ...globalTaskHoursMap[taskId]
                        };
                    }
                });
            }
        });

        // Calculate projectTotal and revisedProjectTotal hours
        const { plannedEffort, actualHours } = calculateTotalProjectHours(enhancedSectionData);
        let totalProjectPlannedEffort = plannedEffort;
        let totalProjectActualHours = actualHours;

        const projectTotalVariance = getVariance(totalProjectActualHours, totalProjectPlannedEffort);
        const projectTotalUtilisationRate = getUtilisationRate(totalProjectActualHours, totalProjectPlannedEffort);
        const projectTotalEffortCompletionPercentage = getEffortCompletionPercentage(totalProjectActualHours, totalProjectPlannedEffort);

        // Update projectTotal with hours
        stageData.projectTotal = {
            ...stageData.projectTotal,
            forecastHours: totalProjectPlannedEffort,
            actualHours: totalProjectActualHours,
            forecastHoursDisplay: formatHoursDisplay(totalProjectPlannedEffort),
            actualHoursDisplay: formatHoursDisplay(totalProjectActualHours),
            variance: roundValue(projectTotalVariance, 0),
            utilisationRate: roundValue(projectTotalUtilisationRate, 0),
            effortCompletionPercentageValue: projectTotalEffortCompletionPercentage,
            effortCompletionPercentage: `${projectTotalEffortCompletionPercentage.toFixed(2)}%`
        };

        // Recalculate projectTotal's percentageComplete based on Financials (Total Claimed / Contract),
        // mirroring the revisedProjectTotal fix below, so the "Project Total" row (which reads from
        // stageData.projectTotal, not revisedProjectTotal) also reflects approved progress claims.
        if (toDecimal(stageData.projectTotal.totalSaleAmount).gt(0)) {
            const projectTotalFinancialPercentage = roundValue(
                toDecimal(totalClaimAmount).dividedBy(toDecimal(stageData.projectTotal.totalSaleAmount)).times(100)
            );
            stageData.projectTotal.percentageComplete = projectTotalFinancialPercentage;
            stageData.projectTotal.completionPercentage = `${roundValue(projectTotalFinancialPercentage, 0)}%`;
        }

        // Enrich variation hours into option stage summary and project total
        if (optionCalculation) {
            // 1. Enrich stage summaries (Variation Total rows)
            if (optionCalculation.stageSummary) {
                Object.keys(optionCalculation.stageSummary).forEach(stageId => {
                    const summary = optionCalculation.stageSummary[stageId];
                    const f = summary.forecastHours || 0;
                    const a = summary.actualHours || 0;
                    const v = getVariance(a, f);
                    const u = getUtilisationRate(a, f);
                    const c = getEffortCompletionPercentage(a, f);

                    optionCalculation.stageSummary[stageId] = {
                        ...summary,
                        forecastHoursDisplay: formatHoursDisplay(f),
                        actualHoursDisplay: formatHoursDisplay(a),
                        variance: roundValue(v, 0),
                        utilisationRate: roundValue(u, 0),
                        effortCompletionPercentageValue: c,
                        effortCompletionPercentage: `${c.toFixed(2)}%`,
                    };

                    // 2. Enrich individual variations within this stage's optionList (Object map)
                    if (summary.optionList && typeof summary.optionList === 'object') {
                        Object.keys(summary.optionList).forEach(optionId => {
                            const variationItem = summary.optionList[optionId];
                            const enhancedV = enhancedVariationData.find(v => String(v.id) === String(optionId));
                            if (enhancedV) {
                                Object.assign(variationItem, {
                                    forecastHours: enhancedV.forecastHours,
                                    actualHours: enhancedV.actualHours,
                                    forecastHoursDisplay: enhancedV.forecastHoursDisplay,
                                    actualHoursDisplay: enhancedV.actualHoursDisplay,
                                    variance: enhancedV.variance,
                                    utilisationRate: enhancedV.utilisationRate,
                                    effortCompletionPercentageValue: enhancedV.effortCompletionPercentageValue,
                                    effortCompletionPercentage: enhancedV.effortCompletionPercentage,
                                });
                            }
                        });
                    }
                });
            }

            // 3. Enrich overall options project total
            const of = optionCalculation.projectTotal?.forecastHours || 0;
            const oa = optionCalculation.projectTotal?.actualHours || 0;
            const ov = getVariance(oa, of);
            const ou = getUtilisationRate(oa, of);
            const oc = getEffortCompletionPercentage(oa, of);

            optionCalculation.projectTotal = {
                ...optionCalculation.projectTotal,
                forecastHoursDisplay: formatHoursDisplay(of),
                actualHoursDisplay: formatHoursDisplay(oa),
                variance: roundValue(ov, 0),
                utilisationRate: roundValue(ou, 0),
                effortCompletionPercentageValue: oc,
                effortCompletionPercentage: `${oc.toFixed(2)}%`,
            };
        }

        // Add hours from ALL variations (approved) to totals for the Revised Project Total
        let finalPlannedEffort = toDecimal(totalProjectPlannedEffort);
        let finalActualHours = toDecimal(totalProjectActualHours);

        enhancedVariationData.forEach(variation => {
            finalPlannedEffort = finalPlannedEffort.plus(toDecimal(variation.forecastHours || 0));
            finalActualHours = finalActualHours.plus(toDecimal(variation.actualHours || 0));
        });

        const revisedProjectTotalVariance = getVariance(finalActualHours.toNumber(), finalPlannedEffort.toNumber());
        const revisedProjectTotalUtilisationRate = getUtilisationRate(finalActualHours.toNumber(), finalPlannedEffort.toNumber());
        const revisedProjectTotalEffortCompletionPercentage = getEffortCompletionPercentage(finalActualHours.toNumber(), finalPlannedEffort.toNumber());

        // Update revisedProjectTotal with summed hours from both project and variations
        revisedProjectTotal = {
            ...revisedProjectTotal,
            forecastHours: finalPlannedEffort.toNumber(),
            actualHours: finalActualHours.toNumber(),
            forecastHoursDisplay: formatHoursDisplay(finalPlannedEffort.toNumber()),
            actualHoursDisplay: formatHoursDisplay(finalActualHours.toNumber()),
            variance: roundValue(revisedProjectTotalVariance, 0),
            utilisationRate: roundValue(revisedProjectTotalUtilisationRate, 0),
            effortCompletionPercentageValue: revisedProjectTotalEffortCompletionPercentage,
            effortCompletionPercentage: `${revisedProjectTotalEffortCompletionPercentage.toFixed(2)}%`
        };

        // Recalculate percentageComplete based on Financials (Total Claimed / Revised Contract)
        // This ensures Section-level claims are captured even if task percentages aren't updated
        if (toDecimal(revisedProjectTotal.totalSaleAmount).gt(0)) {
            const financialPercentage = roundValue(
                toDecimal(totalClaimAmount).dividedBy(toDecimal(revisedProjectTotal.totalSaleAmount)).times(100)
            );
            revisedProjectTotal.percentageComplete = financialPercentage;
            revisedProjectTotal.completionPercentage = `${roundValue(financialPercentage, 0)}%`;
        }

        // Also update stage-level percentageComplete if it's 0 but there are claims
        // This fixes the issue where an Attributed Section has claims but 0% task completion
        if (progressClaims && Array.isArray(progressClaims)) {
            const sectionClaimsDataByStage = {};

            // 1. Build a taskId -> stageId mapping from sectionData for task-level claims
            const taskToStageMap = {};
            const buildTaskStageMapping = (stagesArray) => {
                stagesArray.forEach(stage => {
                    if (stage.stages && Array.isArray(stage.stages)) {
                        buildTaskStageMapping(stage.stages);
                    } else if (stage.tasks && Array.isArray(stage.tasks)) {
                        stage.tasks.forEach(task => {
                            if (task.id) {
                                taskToStageMap[task.id] = stage.id;
                            }
                        });
                    }
                });
            };
            buildTaskStageMapping(sectionData);

            // 2. Build parent-child relationships
            const stageParentMap = {};
            const buildParentMap = (stagesArray, parentId = null) => {
                stagesArray.forEach(stage => {
                    if (parentId) {
                        stageParentMap[stage.id] = parentId;
                    }
                    if (stage.stages && Array.isArray(stage.stages)) {
                        buildParentMap(stage.stages, stage.id);
                    }
                });
            };
            buildParentMap(sectionData);

            // 3. Helper to get all ancestors for a given stageId (including the stage itself)
            const getAllAncestors = (stageId) => {
                const ancestors = [stageId];
                let currentId = stageId;
                while (stageParentMap[currentId]) {
                    ancestors.push(stageParentMap[currentId]);
                    currentId = stageParentMap[currentId];
                }
                return ancestors;
            };

            // 4. Aggregate claims by stageId
            progressClaims.forEach(claim => {
                const tasks = claim.progressClaimTask || claim.tbl_progress_claim_tasks;
                if (claim.progressClaimStatus && claim.progressClaimStatus != 'Draft' && Array.isArray(tasks)) {
                    // Protections against double counting within a single claim
                    const stagesWithSectionClaims = new Set(
                        tasks.filter(t => !t.isDelete && t.type === 'SECTION').map(t => t.stageId)
                    );

                    tasks.filter(t => !t.isDelete).forEach(taskClaim => {
                        let targetStageId = null;
                        if (taskClaim.stageId) {
                            targetStageId = taskClaim.stageId;
                        } else if (taskClaim.taskId) {
                            targetStageId = taskToStageMap[taskClaim.taskId];
                        }

                        if (targetStageId) {
                            // Accuracy check: if this is a task claim, only count it if its stage doesn't have a section claim
                            if (taskClaim.type === 'SECTION' || !stagesWithSectionClaims.has(taskClaim.stageId)) {
                                const claimAmount = parseFloat(taskClaim.total) || 0;
                                const ancestors = getAllAncestors(targetStageId);
                                ancestors.forEach(ancestorId => {
                                    sectionClaimsDataByStage[ancestorId] = (sectionClaimsDataByStage[ancestorId] || 0) + claimAmount;
                                });
                            }
                        }
                    });
                }
            });

            // 5. Update ALL section percentages using financial completion (claim / sale)
            if (stageData.sections) {
                Object.keys(stageData.sections).forEach(sectionId => {
                    const section = stageData.sections[sectionId];
                    const claimTotal = sectionClaimsDataByStage[sectionId] || 0;

                    if (section.totalSaleAmount > 0 && claimTotal > 0) {
                        const financialPercentage = Math.min(roundValue((claimTotal / section.totalSaleAmount) * 100), 100);
                        section.percentageComplete = financialPercentage;
                        // Non-nested sections render from taskList.total (see project-task-list.jsx
                        // hasNestedStages check), so this must be kept in sync too.
                        if (section.taskList && section.taskList.total) {
                            section.taskList.total.percentageComplete = financialPercentage;
                        }
                        // Propagate down to each individual task row too - otherwise the "Total"/
                        // section-level completion % is fixed but each task row (which reads its own
                        // taskList.tasks[taskId].percentageComplete, an unrelated stale field from the
                        // task record) keeps showing its old, pre-claim value even though the Budget
                        // page (which derives per-task completion live from claim data) shows 100%.
                        if (section.taskList && section.taskList.tasks) {
                            Object.values(section.taskList.tasks).forEach(task => {
                                task.percentageComplete = financialPercentage;
                            });
                        }
                    }
                });
            }
        }

        dispatch(addSectionDataAction(enhancedSectionData));
        dispatch(addOptionDataAction(displayableOptionData));
        dispatch(addVariationDataAction(enhancedVariationData));
        dispatch(addProjectVariationAction(enhancedProjectLevelVariation));
        dispatch(addSectionVariationAction(enhancedSectionLevelVariation));
        dispatch(addProjectTotalAction(stageData.projectTotal));
        dispatch(addRevisedProjectTotalAction(revisedProjectTotal));
        dispatch(addSectionsAction(stageData.sections));
        dispatch(addOptionsAction(approvedOptionCalculation));

        delete projectData.stages;

        dispatch(addProjectAction({
            projectId: projectData.id,
            projectName: projectData.name,
            projectManagerName: idx(projectData, _ => _.manager.name),
            quoteType: projectData.quoteType,
            projectDescription: projectData.description,
            customerName: projectData.customer && projectData.customer.businessName,
            hasStages: projectData.hasStages,
            projectType: projectData.type,
            projectDataType: projectData.projectType,
            level: projectData.level,
            customerLevel: projectData.customer ? projectData.customer.level : "",
            contactData: projectData.customerContact,
            projectMarginValue: projectData.profitMarginValue || "",
            siteAddress: projectData.address,
            projectDueDate: projectData.dateOfCreation,
            attachmentArr: projectData.attachment,
            showStageSelect,
            totalApprovedInvoices,
            res: projectData
        }))

    });
}


export const getLevels = async ({ levelId, dispatch }) => {
    const level = await dispatch(getLevel(levelId))

    const labors = [];
    level.hourlyCost && level.hourlyCost.forEach((cost) => {
        labors.push({
            ...cost,
            calloutCost: level.calloutCost,
            travelCost: level.travelCost,
        });
    });

    dispatch(addLevelAction(labors))
};

export const calculateActualBudget = (task = {}, laborCost = {}) => {
    const actualMaterialCost =
        (Array.isArray(task.materialPoCost) &&
            task.materialPoCost.reduce((acc, _material) => {
                let materialBudget = 0;
                if (_material && _material.budget && "PO" in _material.budget) {
                    materialBudget += (Array.isArray(_material.budget["PO"]) &&
                        _material.budget["PO"].reduce((acc, poBudget) =>
                            acc + parseFloat(poBudget.amountPaid),
                            0
                        )) || 0;
                }
                if (_material && _material.budget && "WPO" in _material.budget) {
                    materialBudget += (Array.isArray(_material.budget["WPO"]) &&
                        _material.budget["WPO"].reduce((acc, poBudget) =>
                            acc + parseFloat(poBudget.amountPaid),
                            0
                        )) || 0;
                }
                return acc + materialBudget;
            }, 0)) || 0;
    const actualLaborCost = idx(laborCost, (_laborCost) => _laborCost[task.id]) || (task.laborCost ? (parseFloat(task.laborCost) || 0) : 0);
    const actualSubContractorCost = (Array.isArray(task.subContractorInvoiceCost) &&
        task.subContractorInvoiceCost.reduce((acc, _invoices) => {
            const claim = _invoices.invoiceClaim !== undefined && _invoices.invoiceClaim !== null
                ? _invoices.invoiceClaim
                : (_invoices.amountPaid || 0);
            return acc + parseFloat(claim);
        }, 0)) || 0;

    return { actualMaterialCost, actualLaborCost, actualSubContractorCost };
};